import { useEffect, useState, useContext, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { ConfigContext } from "../util/ConfigContext";
import { ThemeContext } from "../util/ThemeContext";
import logo from "../assets/logo.png";
import { tailChase } from "ldrs";
import API_URL from "../config/apiConfig";
import { getJwtData } from "../util/JwtUtil";
import LoadingOverlay from "./LoadingOverlay";
import detectedPlatform from "../util/PlatformUtils";

tailChase.register();

let deviceHeaders = {};

function safeParse(jsonStr) {
  try {
    return typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
  } catch {
    return {};
  }
}

// ✅ Platform detection (optimized)
// eslint-disable-next-line no-undef
if (typeof ZoomHelper !== "undefined" && ZoomHelper.getDeviceInfo) {
  deviceHeaders["x-detected-platform"] = "android";
  try {
    // eslint-disable-next-line no-undef
    const rawInfo = ZoomHelper.getDeviceInfo();
    const info = safeParse(rawInfo);
    const device = info?.device || {};
    Object.assign(deviceHeaders, {
      "x-device-uuid": device.uuid || "",
      "x-device-model": encodeURIComponent(
        `${device.model || "unknown"}-${device.manufacturer || "unknown"}-${
          device.android_version || "unknown"
        }`,
      ),
    });
  } catch (err) {
    console.error("Device info error:", err);
  }
} else if (window?.nativeBridge?.send || window?.chrome?.webview?.postMessage) {
  deviceHeaders["x-detected-platform"] = "windows";
  // eslint-disable-next-line no-undef
} else if (typeof webkit !== "undefined" && webkit?.messageHandlers?.callback) {
  deviceHeaders["x-detected-platform"] = "ios";
} else {
  deviceHeaders["x-detected-platform"] = "web";
}

async function tryLogin({ username, password }) {
  const response = await fetch(`${API_URL}/auth/universal/login`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...deviceHeaders,
    },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData?.data?.errorMessage || errorData?.message || "Login failed.",
    );
  }
  return response.json();
}

function LoginHybrid() {
  const [error, setError] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { jwt, readySent, setJwt, setReadySent, setRawJwt } =
    useContext(ConfigContext);
  const themeData = useContext(ThemeContext);

  // ✅ Preload images for instant rendering
  useEffect(() => {
    const preload = (src) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
    };
    preload(themeData?.background_doodleUrl);
    preload(themeData?.lightlogoUrl || logo);
  }, [themeData]);

  // ✅ Memoize URLs to prevent repeated image fetches
  const bgUrl = useMemo(
    () => themeData?.background_doodleUrl || "",
    [themeData?.background_doodleUrl],
  );
  const logoUrl = useMemo(
    () => themeData?.lightlogoUrl || logo,
    [themeData?.lightlogoUrl],
  );

  // useEffect(() => {
  //   // ✅ Notify Android WebView once when page is fully loaded
  //   const timer = setTimeout(() => {
  //     sendAndroidPageReadySignal();
  //   }, 100);
  //   return () => clearTimeout(timer);
  // }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let u = params.get("username");
    let p = params.get("password");

    if (u || p) {
      if (u) setUsername(u);
      if (p) setPassword(p);
    } else {
      u = localStorage.getItem("savedUsername");
      p = localStorage.getItem("savedPassword");
      if (u) setUsername(u);
      if (p) setPassword(p);
    }

    if (!readySent) {
      setReadySent(true);
      sendPageReadySignal();
    }
    if (jwt) {
      // ✅ User is already logged in — navigate immediately
      if (jwt.role === "SUBSCRIBER" || jwt.role === "HOST") {
        navigate("/schedule");
      } else {
        navigate("/dashboard");
      }
    } else {
      setIsCheckingAuth(false);
    }
  }, [navigate, jwt]);

  // ✅ Android TV remote input handling
  useEffect(() => {
    if (
      detectedPlatform() === "android" &&
      window?.ZoomHelper?.isAndroidTv?.()
    ) {
      const timeout = setTimeout(() => {
        document.querySelector("#submitButton")?.focus();
      }, 300);

      const inputs = [
        document.getElementById("username"),
        document.getElementById("password"),
      ];
      const cleanupFns = [];

      inputs.forEach((input) => {
        if (input) {
          const handler = (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              input.focus();
            }
          };
          input.addEventListener("keydown", handler);
          cleanupFns.push(() => input.removeEventListener("keydown", handler));
        }
      });

      return () => {
        cleanupFns.forEach((fn) => fn());
        clearTimeout(timeout);
      };
    }
  }, []);

  // ✅ Login mutation
  const mutation = useMutation({
    mutationFn: tryLogin,
    onSuccess: (data) => {
      setIsCheckingAuth(true);
      setRawJwt(data.token);
      const jwtData = getJwtData(data.token);
      setJwt(jwtData);

      const platform = detectedPlatform();
      switch (platform) {
        case "android":
          window.ZoomHelper?.updateTenantTagForPushNotification?.(
            jwtData?.un,
            jwtData?.tenant,
          );
          break;
        case "ios":
          window.webkit?.messageHandlers?.registerPush?.postMessage({
            userId: jwtData?.un,
            tenant: jwtData?.tenant,
          });
          break;
        default:
          break;
      }

      localStorage.setItem("savedUsername", username);
      localStorage.setItem("savedPassword", password);
      setIsCheckingAuth(false);
    },
    onError: (error) => {
      setError(error.message);
      setIsCheckingAuth(false);
    },
  });

  // ✅ Helper: Send signal to Android WebView when page is ready
  function sendPageReadySignal(retries = 5) {
    if (window.PageLoadHelper?.onPageFullyLoaded) {
      window.PageLoadHelper.onPageFullyLoaded();
      return;
    }

    if (window.webkit?.messageHandlers?.hideSplash) {
      window.webkit.messageHandlers.hideSplash.postMessage("ready");
      return;
    }

    if (retries > 0) {
      setTimeout(() => sendPageReadySignal(retries - 1), 300);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({ username, password });
  };

  if (isCheckingAuth) return <LoadingOverlay type={1} />;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-4 overflow-hidden bg-dashboardActiveTabBackgroundColor"
      style={{
        backgroundImage: bgUrl ? `url(${bgUrl})` : "none",
        backgroundRepeat: "repeat",
        backgroundSize: "contain",
        backgroundPosition: "center",
        imageRendering: "auto",
      }}
    >
      {/* 🔒 Login Card */}
      <div className="relative z-10 w-full max-w-md bg-loginCardBackground opacity-95 backdrop-blur-md rounded-xl shadow-xl overflow-hidden">
        <div className="relative z-20 p-8 space-y-6">
          <img
            src={logoUrl}
            alt="logo"
            loading="lazy"
            decoding="async"
            className="mx-auto h-24 w-24 sm:h-32 sm:w-32 md:h-40 md:w-40 lg:h-48 lg:w-48 drop-shadow-md"
          />

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div className="relative w-full">
              <input
                id="username"
                name="username"
                type="text"
                required
                placeholder="Username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full h-12 rounded-md text-center text-base px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary border transition-all duration-150"
                style={{
                  backgroundColor:
                    themeData?.theme?.loginInputAreaBackgroundColor || "#fff",
                  color: themeData?.theme?.loginInputTypedTextColor || "#000",
                  borderColor: themeData?.theme?.primary || "#ccc",
                }}
              />
            </div>

            {/* Password */}
            <div className="relative w-full">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                placeholder="Password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 rounded-md text-center text-base px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary border transition-all duration-150"
                style={{
                  backgroundColor:
                    themeData?.theme?.loginInputAreaBackgroundColor || "#fff",
                  color: themeData?.theme?.loginInputTypedTextColor || "#000",
                  borderColor: themeData?.theme?.primary || "#ccc",
                }}
              />

              {/* 👁 Toggle Button */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                tabIndex={-1}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={showPassword ? "hide" : "show"}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center justify-center"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </motion.div>
                </AnimatePresence>
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              id="submitButton"
              disabled={mutation.isPending}
              className={`w-full bg-loginButtonColor hover:bg-loginButtonHoverColor hover:text-loginButtonHoverTextColor text-loginButtonTextColor font-semibold py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-loginButtonColor transition-all duration-200 ${
                mutation.isPending ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {mutation.isPending ? "Logging in..." : "SUBMIT"}
            </button>
          </form>

          {error && (
            <p className="text-red-600 text-sm font-semibold text-center">
              {error}
            </p>
          )}
        </div>
      </div>

      <style>{`
        html, body {
          margin: 0;
          padding: 0;
          height: 100%;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default LoginHybrid;
